(() => {
  if (customElements.get('styled-by-you')) return;
  class StyledByYou extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.dialog = this.querySelector('dialog');
      this.items = [...this.querySelectorAll('[data-gallery-item]')];
      this.dialogItems = this.items.filter(item => item.dataset.kind === 'photo');
      this.player = this.querySelector('[data-player]');
      this.status = this.querySelector('[data-status]');
      this.counter = this.querySelector('[data-counter]');
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.setupInline(options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.player.querySelector('video')?.pause();
        this.updateInlinePlayback();
      }, options);
      if (typeof this.dialog.showModal !== 'function') return;
      this.dialogItems.forEach((item, index) => {
        item.querySelector('[data-open]')?.addEventListener('click', event => {
          event.preventDefault();
          this.open(index);
        }, options);
      });
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close(), options);
      this.previous.addEventListener('click', () => this.show(this.index - 1), options);
      this.next.addEventListener('click', () => this.show(this.index + 1), options);
      this.previous.parentElement.hidden = this.dialogItems.length < 2;
      this.dialog.addEventListener('close', () => this.clear(true), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.target.closest('video, input, select, textarea')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
        }
      }, options);
    }
    setupInline(options) {
      this.observer?.disconnect();
      this.observer = null;
      this.inlineStates = this.items.filter(item => item.dataset.kind === 'video').map(item => ({
        item, button: item.querySelector('[data-inline-audio]'), host: item.querySelector('[data-inline-player]'),
        playback: item.querySelector('[data-inline-playback]'),
        visible: false, blocked: false, pending: false, request: 0, manualPlay: false, userPaused: false,
      })).filter(state => state.button && state.host);
      this.inlineActive = null;
      this.motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      this.motion?.addEventListener('change', () => this.updateInlinePlayback(), options);
      this.inlineStates.forEach(state => {
        state.button.hidden = true;
        if (state.playback) {
          state.playback.hidden = false;
          state.playback.addEventListener('click', () => {
            if (!this.isConnected || document.hidden || this.dialog.open || (this.observer && !state.visible)) return;
            if (state.video && !state.video.paused || state.pending) {
              state.userPaused = true;
            } else {
              state.userPaused = false;
              state.manualPlay = true;
              state.blocked = false;
              this.inlineActive = state;
              if (!this.observer) state.visible = true;
            }
            this.updateInlinePlayback();
          }, options);
        }
        state.button.addEventListener('click', () => {
          if (this.inlineActive !== state || !state.video || state.video.paused) return;
          state.video.muted = !state.video.muted;
          this.paintInline(state, true);
        }, options);
      });
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(entries => {
        if (this.observer !== observer || !this.isConnected) return;
        for (const entry of entries) {
          const state = this.inlineStates.find(candidate => candidate.item === entry.target);
          if (state) {
            state.visible = entry.isIntersecting && entry.intersectionRatio > 0;
          }
        }
        this.updateInlinePlayback();
      }, { threshold: [0, 0.01] });
      this.observer = observer;
      this.inlineStates.forEach(state => this.observer.observe(state.item));
    }
    updateInlinePlayback() {
      const eligible = state => state.visible && !state.blocked && !state.userPaused && (!this.motion?.matches || state.manualPlay);
      const canPlay = this.isConnected && !document.hidden && !this.dialog.open;
      const active = canPlay ? (this.inlineStates.find(state => state === this.inlineActive && eligible(state))
        || this.inlineStates.find(eligible)) : null;
      this.inlineActive = active;
      this.inlineStates.forEach(state => {
        if (state === active) this.playInline(state);
        else this.pauseInline(state);
      });
    }
    paintInline(state, playing) {
      const muted = state.video?.muted !== false;
      state.button.hidden = !playing;
      state.button.setAttribute('aria-label', muted ? state.button.dataset.unmuteLabel : state.button.dataset.muteLabel);
      state.button.setAttribute('aria-pressed', String(!muted));
      state.button.querySelector('[data-muted-icon]').toggleAttribute('hidden', !muted);
      state.button.querySelector('[data-sound-icon]').toggleAttribute('hidden', muted);
      if (state.playback) {
        state.playback.dataset.state = playing ? 'playing' : 'paused';
        state.playback.setAttribute('aria-label', state.blocked ? 'Retry video' : playing ? 'Pause video' : 'Play video');
        state.playback.querySelector('[data-inline-play]').toggleAttribute('hidden', playing);
        state.playback.querySelector('[data-inline-pause]').toggleAttribute('hidden', !playing);
      }
    }
    pauseInline(state) {
      if (state.pending || (state.video && !state.video.paused)) {
        state.request++;
        state.pending = false;
        state.video?.pause();
      }
      if (state.video) state.video.muted = true;
      this.paintInline(state, false);
    }
    recoverInline(state) {
      queueMicrotask(() => {
        if (this.isConnected && this.inlineStates.includes(state)) this.updateInlinePlayback();
      });
    }
    playInline(state) {
      if (state.pending || (state.video && !state.video.paused)) return;
      if (!state.video) {
        state.video = state.item.querySelector('[data-media-template]').content.querySelector('video').cloneNode(true);
        const video = state.video;
        video.controls = false;
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.tabIndex = -1;
        video.className = 'StyledByYou__inlineVideo';
        state.host.replaceChildren(video);
        video.addEventListener('playing', () => {
          if (this.inlineActive !== state) return this.pauseInline(state);
          state.host.hidden = false;
          this.paintInline(state, true);
        }, { signal: this.abort.signal });
        video.addEventListener('volumechange', () => {
          this.paintInline(state, this.inlineActive === state && !video.paused);
        }, { signal: this.abort.signal });
        video.addEventListener('pause', () => {
          if (!video.paused) return;
          video.muted = true;
          this.paintInline(state, false);
        }, { signal: this.abort.signal });
        video.addEventListener('error', () => {
          state.blocked = true;
          this.pauseInline(state);
          state.host.hidden = true;
          if (this.inlineActive === state) this.inlineActive = null;
          this.recoverInline(state);
        }, { signal: this.abort.signal });
      }
      const video = state.video;
      // Expose the inline video before requesting playback, including in Safari.
      state.host.hidden = false;
      const request = ++state.request;
      state.pending = true;
      const failed = () => {
        if (state.request !== request) return;
        state.pending = false;
        state.blocked = true;
        state.host.hidden = true;
        this.paintInline(state, false);
        if (this.inlineActive === state) this.inlineActive = null;
        this.recoverInline(state);
      };
      try {
        if (video.error) video.load();
        Promise.resolve(video.play()).then(() => {
          if (this.inlineActive !== state) video.pause();
          if (state.request === request) state.pending = false;
        }, failed);
      } catch { failed(); }
    }
    open(index) {
      if (this.dialog.open || !this.dialogItems[index]) return;
      this.trigger = this.dialogItems[index].querySelector('[data-open]');
      this.dialog.showModal();
      this.updateInlinePlayback();
      this.show(index);
    }
    show(index) {
      if (!this.dialog.open || index < 0 || index >= this.dialogItems.length) return;
      this.clear(false);
      this.index = index;
      const item = this.dialogItems[index];
      this.dialog.setAttribute('aria-label', item.dataset.label);
      this.counter.textContent = `${index + 1} / ${this.dialogItems.length}`;
      this.previous.disabled = index === 0;
      this.next.disabled = index === this.dialogItems.length - 1;
      if (document.activeElement === this.previous && this.previous.disabled) this.next.focus({ preventScroll: true });
      if (document.activeElement === this.next && this.next.disabled) this.previous.focus({ preventScroll: true });
      this.player.replaceChildren(item.querySelector('[data-media-template]').content.cloneNode(true));
      const media = this.player.querySelector('video, img');
      const isCurrent = () => this.dialog.open && this.player.contains(media);
      media.addEventListener('error', () => {
        if (isCurrent()) this.status.textContent = 'This content could not load. Close it and try again.';
      }, { signal: this.mediaAbort.signal });
      if (media.tagName !== 'VIDEO') return;
      media.muted = true;
      media.play()?.catch(() => {
        if (isCurrent()) this.status.textContent = 'Press play to watch the video.';
      });
    }
    clear(restoreFocus = false) {
      this.mediaAbort?.abort();
      this.mediaAbort = new AbortController();
      this.player.querySelector('video')?.pause();
      this.player.replaceChildren();
      this.status.textContent = '';
      if (restoreFocus && this.isConnected) {
        this.trigger?.focus({ preventScroll: true });
        this.updateInlinePlayback();
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.observer = null;
      this.inlineActive = null;
      this.inlineStates?.forEach(state => {
        this.pauseInline(state);
        if (state.video) {
          state.video.removeAttribute('src');
          state.video.replaceChildren();
          state.video.load();
        }
        state.host.replaceChildren();
        state.host.hidden = true;
        state.button.hidden = true;
      });
      if (this.dialog?.open) this.dialog.close();
      if (this.player) this.clear();
      this.mediaAbort?.abort();
    }
  }
  customElements.define('styled-by-you', StyledByYou);
})();
